import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { API, resolveImageUrl } from "../App";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Filter, X, ChevronDown, ChevronUp } from "lucide-react";

// ── Sort options ──────────────────────────────────────────────────────────────

const SORT_OPTIONS = [
  { label: "Featured",           value: "featured"   },
  { label: "Newest",             value: "newest"     },
  { label: "Price: Low to High", value: "price_asc"  },
  { label: "Price: High to Low", value: "price_desc" },
  { label: "Best Sellers",       value: "best_sellers"},
  { label: "Discount",           value: "discount"   },
];

const PRICE_RANGES = [
  { label: "Under ₹1,000",         min: 0,    max: 999   },
  { label: "₹1,000 – ₹1,999",      min: 1000, max: 1999  },
  { label: "₹2,000 – ₹2,999",      min: 2000, max: 2999  },
  { label: "₹3,000 & above",        min: 3000, max: Infinity },
];

// ── Sort helper ───────────────────────────────────────────────────────────────

function sortProducts(products, sortValue) {
  const arr = [...products];
  switch (sortValue) {
    case "price_asc":     return arr.sort((a, b) => a.price - b.price);
    case "price_desc":    return arr.sort((a, b) => b.price - a.price);
    case "best_sellers":  return arr.sort((a, b) => (b.is_best_seller ? 1 : 0) - (a.is_best_seller ? 1 : 0));
    case "newest":        return arr.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    case "discount":      return arr.sort((a, b) => {
      const discA = a.compare_at_price > a.price ? a.compare_at_price - a.price : 0;
      const discB = b.compare_at_price > b.price ? b.compare_at_price - b.price : 0;
      return discB - discA;
    });
    default: return arr;
  }
}

// ── Collapsible filter section ────────────────────────────────────────────────

function FilterSection({ title, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-zinc-100 pb-5 mb-5">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center justify-between w-full mb-3"
      >
        <h3 className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-medium">{title}</h3>
        {open ? <ChevronUp size={14} className="text-zinc-400" /> : <ChevronDown size={14} className="text-zinc-400" />}
      </button>
      {open && children}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function ShopPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts]         = useState([]);
  const [categories, setCategories]     = useState([]);
  const [loading, setLoading]           = useState(true);

  // ── Filter state ──────────────────────────────────────────────────────────
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get("category") || "");
  const [selectedPriceRange, setSelectedPriceRange] = useState(null); // index into PRICE_RANGES or null
  const [onlyBestSellers, setOnlyBestSellers]   = useState(searchParams.get("best_seller") === "true");
  const [onlyInStock, setOnlyInStock]           = useState(false);
  const [onlyOnSale, setOnlyOnSale]             = useState(false);

  // ── UI state ─────────────────────────────────────────────────────────────
  const [sortValue, setSortValue]         = useState("featured");
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);
  const sortRef = useRef(null);

  // ── Active filter count (for badge on mobile button) ──────────────────────
  const activeFilterCount = [
    selectedCategory !== "",
    selectedPriceRange !== null,
    onlyBestSellers,
    onlyInStock,
    onlyOnSale,
  ].filter(Boolean).length;

  const hasActiveFilters = activeFilterCount > 0;

  // Close sort dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (sortRef.current && !sortRef.current.contains(e.target)) setSortDropdownOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Lock body scroll when mobile filter drawer is open
  useEffect(() => {
    document.body.style.overflow = mobileFilterOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileFilterOpen]);

  useEffect(() => { fetchCategories(); }, []);

  // Fetch all products once; filtering is done client-side for instant results
  useEffect(() => { fetchProducts(); }, []);

  // Sync filter state when URL search params change (e.g. footer/navbar links)
  // Also scroll to top so the new category starts from the beginning
  useEffect(() => {
    setSelectedCategory(searchParams.get("category") || "");
    setOnlyBestSellers(searchParams.get("best_seller") === "true");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [searchParams]);

  const fetchCategories = async () => {
    try {
      const { data } = await axios.get(`${API}/categories`);
      setCategories(data);
    } catch (error) {
      console.error("Failed to fetch categories", error);
    }
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/products`);
      setProducts(data);
    } catch (error) {
      console.error("Failed to fetch products", error);
    } finally {
      setLoading(false);
    }
  };

  // ── Client-side filtering ─────────────────────────────────────────────────
  const filteredProducts = products.filter((p) => {
    if (selectedCategory && p.category !== selectedCategory) return false;
    if (onlyBestSellers && !p.is_best_seller)               return false;
    if (onlyInStock && !(p.stock > 0))                       return false;
    if (onlyOnSale && !(p.compare_at_price > p.price))       return false;
    if (selectedPriceRange !== null) {
      const range = PRICE_RANGES[selectedPriceRange];
      if (p.price < range.min || p.price > range.max) return false;
    }
    return true;
  });

  const sortedProducts = sortProducts(filteredProducts, sortValue);
  const activeSortLabel = SORT_OPTIONS.find((o) => o.value === sortValue)?.label ?? "Featured";

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    const params = {};
    if (category)       params.category    = category;
    if (onlyBestSellers) params.best_seller = "true";
    setSearchParams(params);
    setMobileFilterOpen(false);
  };

  const clearAllFilters = () => {
    setSelectedCategory("");
    setSelectedPriceRange(null);
    setOnlyBestSellers(false);
    setOnlyInStock(false);
    setOnlyOnSale(false);
    setSearchParams({});
  };

  // ── Sidebar filter content (shared between desktop + mobile drawer) ────────
  const FilterContent = () => (
    <div>
      {/* ── Category ──────────────────────────────────────────────────── */}
      <FilterSection title="Category">
        <div className="space-y-0.5">
          <button
            onClick={() => handleCategoryChange("")}
            className={`block w-full text-left py-2 px-3 text-sm transition-colors rounded-sm ${
              selectedCategory === ""
                ? "bg-zinc-900 text-white"
                : "text-zinc-600 hover:text-zinc-900 hover:bg-zinc-50"
            }`}
            data-testid="category-all"
          >
            All Products
          </button>
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => handleCategoryChange(category)}
              className={`block w-full text-left py-2 px-3 text-sm transition-colors rounded-sm ${
                selectedCategory === category
                  ? "bg-zinc-900 text-white"
                  : "text-zinc-600 hover:text-zinc-900 hover:bg-zinc-50"
              }`}
              data-testid={`category-filter-${category.toLowerCase().replace(" ", "-")}`}
            >
              {category}
            </button>
          ))}
        </div>
      </FilterSection>

      {/* ── Price Range ───────────────────────────────────────────────── */}
      <FilterSection title="Price Range">
        <div className="space-y-0.5">
          {PRICE_RANGES.map((range, idx) => (
            <button
              key={idx}
              onClick={() => setSelectedPriceRange(selectedPriceRange === idx ? null : idx)}
              className={`flex items-center justify-between w-full py-2 px-3 text-sm transition-colors rounded-sm ${
                selectedPriceRange === idx
                  ? "bg-zinc-900 text-white"
                  : "text-zinc-600 hover:text-zinc-900 hover:bg-zinc-50"
              }`}
              data-testid={`price-range-${idx}`}
            >
              <span>{range.label}</span>
              {/* product count badge */}
              <span className={`text-xs rounded-full px-1.5 ${
                selectedPriceRange === idx ? "bg-white/20 text-white" : "bg-zinc-100 text-zinc-400"
              }`}>
                {products.filter(p =>
                  p.price >= range.min && p.price <= range.max &&
                  (selectedCategory ? p.category === selectedCategory : true)
                ).length}
              </span>
            </button>
          ))}
        </div>
      </FilterSection>

      {/* ── Availability ──────────────────────────────────────────────── */}
      <FilterSection title="Availability">
        <div className="space-y-3">
          <label className="flex items-center gap-3 cursor-pointer group">
            <input
              type="checkbox"
              checked={onlyInStock}
              onChange={() => setOnlyInStock((v) => !v)}
              className="w-4 h-4 accent-zinc-900"
              data-testid="filter-in-stock"
            />
            <span className="text-sm text-zinc-600 group-hover:text-zinc-900">In Stock Only</span>
          </label>
        </div>
      </FilterSection>

      {/* ── Offers ────────────────────────────────────────────────────── */}
      <FilterSection title="Offers">
        <div className="space-y-3">
          <label className="flex items-center gap-3 cursor-pointer group">
            <input
              type="checkbox"
              checked={onlyBestSellers}
              onChange={() => setOnlyBestSellers((v) => !v)}
              className="w-4 h-4 accent-zinc-900"
              data-testid="filter-best-sellers"
            />
            <span className="text-sm text-zinc-600 group-hover:text-zinc-900">Best Sellers</span>
          </label>
          <label className="flex items-center gap-3 cursor-pointer group">
            <input
              type="checkbox"
              checked={onlyOnSale}
              onChange={() => setOnlyOnSale((v) => !v)}
              className="w-4 h-4 accent-zinc-900"
              data-testid="filter-on-sale"
            />
            <span className="text-sm text-zinc-600 group-hover:text-zinc-900">On Sale</span>
          </label>
        </div>
      </FilterSection>

      {/* ── Clear all filters ─────────────────────────────────────────── */}
      {hasActiveFilters && (
        <button
          onClick={clearAllFilters}
          className="w-full mt-2 py-2.5 border border-zinc-300 text-xs uppercase tracking-widest text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900 transition-colors"
          data-testid="clear-all-filters"
        >
          Clear All Filters
        </button>
      )}
    </div>
  );

  return (
    <div className="min-h-screen">
      <Navbar />

      <div className="px-6 md:px-12 max-w-7xl mx-auto py-12">

        {/* ── Page header ───────────────────────────────────────────────── */}
        <div className="mb-10">
          <h1
            className="cormorant text-4xl sm:text-5xl font-light tracking-tight mb-2"
            data-testid="shop-title"
          >
            {selectedCategory || "Shop All"}
          </h1>
          <p className="text-base text-zinc-500">
            Explore our complete collection of 92.5 silver jewellery
          </p>
        </div>

        {/* ── Active filter chips ───────────────────────────────────────── */}
        {hasActiveFilters && (
          <div className="flex flex-wrap gap-2 mb-6" data-testid="active-filter-chips">
            {selectedCategory && (
              <button
                onClick={() => handleCategoryChange("")}
                className="flex items-center gap-1.5 bg-zinc-900 text-white text-xs px-3 py-1.5 uppercase tracking-wide"
              >
                {selectedCategory} <X size={10} />
              </button>
            )}
            {selectedPriceRange !== null && (
              <button
                onClick={() => setSelectedPriceRange(null)}
                className="flex items-center gap-1.5 bg-zinc-900 text-white text-xs px-3 py-1.5 uppercase tracking-wide"
              >
                {PRICE_RANGES[selectedPriceRange].label} <X size={10} />
              </button>
            )}
            {onlyBestSellers && (
              <button
                onClick={() => setOnlyBestSellers(false)}
                className="flex items-center gap-1.5 bg-zinc-900 text-white text-xs px-3 py-1.5 uppercase tracking-wide"
              >
                Best Sellers <X size={10} />
              </button>
            )}
            {onlyInStock && (
              <button
                onClick={() => setOnlyInStock(false)}
                className="flex items-center gap-1.5 bg-zinc-900 text-white text-xs px-3 py-1.5 uppercase tracking-wide"
              >
                In Stock <X size={10} />
              </button>
            )}
            {onlyOnSale && (
              <button
                onClick={() => setOnlyOnSale(false)}
                className="flex items-center gap-1.5 bg-zinc-900 text-white text-xs px-3 py-1.5 uppercase tracking-wide"
              >
                On Sale <X size={10} />
              </button>
            )}
          </div>
        )}

        {/* ── Toolbar: product count + mobile filter btn + sort ─────────── */}
        <div className="flex items-center justify-between mb-8 border-b border-zinc-100 pb-5">

          {/* Product count */}
          <p className="text-sm text-zinc-500" data-testid="product-count">
            {loading
              ? "Loading…"
              : `${sortedProducts.length} product${sortedProducts.length !== 1 ? "s" : ""}${hasActiveFilters ? " (filtered)" : ""}`
            }
          </p>

          <div className="flex items-center gap-3">
            {/* Mobile filter button */}
            <button
              className="lg:hidden flex items-center gap-2 border border-zinc-200 px-4 py-2 text-xs uppercase tracking-widest hover:bg-zinc-50 transition-colors"
              onClick={() => setMobileFilterOpen(true)}
              data-testid="mobile-filter-btn"
            >
              <Filter size={14} />
              Filter
              {activeFilterCount > 0 && (
                <span className="ml-1 bg-zinc-900 text-white text-[10px] px-1.5 py-0.5 rounded-full">
                  {activeFilterCount}
                </span>
              )}
            </button>

            {/* Sort dropdown */}
            <div className="relative" ref={sortRef}>
              <button
                onClick={() => setSortDropdownOpen((v) => !v)}
                className="flex items-center gap-2 border border-zinc-200 px-4 py-2 text-xs uppercase tracking-widest hover:bg-zinc-50 transition-colors"
                data-testid="sort-btn"
              >
                Sort: {activeSortLabel}
                <ChevronDown
                  size={14}
                  className={`transition-transform duration-200 ${sortDropdownOpen ? "rotate-180" : ""}`}
                />
              </button>

              {sortDropdownOpen && (
                <div className="absolute right-0 top-full mt-1 w-52 bg-white border border-zinc-100 shadow-lg z-30">
                  {SORT_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => { setSortValue(option.value); setSortDropdownOpen(false); }}
                      className={`block w-full text-left px-4 py-3 text-xs uppercase tracking-wide transition-colors ${
                        sortValue === option.value
                          ? "bg-zinc-900 text-white"
                          : "text-zinc-700 hover:bg-zinc-50"
                      }`}
                      data-testid={`sort-option-${option.value}`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Main layout ───────────────────────────────────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-12">

          {/* Desktop sidebar */}
          <div className="hidden lg:block lg:col-span-1">
            <div className="sticky top-24">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <Filter size={16} className="text-zinc-400" />
                  <h2 className="text-xs uppercase tracking-widest font-medium text-zinc-700">Filters</h2>
                </div>
                {hasActiveFilters && (
                  <button
                    onClick={clearAllFilters}
                    className="text-xs text-zinc-400 hover:text-zinc-900 underline underline-offset-2 transition-colors"
                  >
                    Clear all
                  </button>
                )}
              </div>
              <FilterContent />
            </div>
          </div>

          {/* Products grid */}
          <div className="lg:col-span-3">
            {loading ? (
              <div className="text-center py-24">
                <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-zinc-900 border-r-transparent"></div>
              </div>

            ) : sortedProducts.length === 0 ? (
              // Only category selected (no other filters) → Coming Soon
              // Other filters active → suggest clearing them
              (selectedCategory && !selectedPriceRange && !onlyBestSellers && !onlyInStock && !onlyOnSale) ? (
                <div className="text-center py-24" data-testid="coming-soon-state">
                  <div className="w-16 h-16 rounded-full bg-[#F5EFE6] flex items-center justify-center mx-auto mb-6">
                    <span className="text-2xl">✨</span>
                  </div>
                  <p className="cormorant text-4xl font-light text-zinc-800 mb-3">
                    Coming Soon
                  </p>
                  <p className="text-sm text-zinc-500 mb-2">
                    We're curating something beautiful for <span className="font-medium text-zinc-700">{selectedCategory}</span>.
                  </p>
                  <p className="text-xs text-zinc-400 mb-8">
                    Check back soon — new pieces are on their way.
                  </p>
                  <button
                    onClick={clearAllFilters}
                    className="inline-block border border-zinc-300 text-zinc-700 hover:bg-zinc-50 px-8 py-3 uppercase tracking-widest text-xs transition-colors"
                  >
                    Browse All Jewellery
                  </button>
                </div>
              ) : (
                <div className="text-center py-24" data-testid="empty-state">
                  <p className="cormorant text-3xl font-light text-zinc-400 mb-3">No pieces found</p>
                  <p className="text-sm text-zinc-500 mb-8">Try adjusting or clearing your filters.</p>
                  <button
                    onClick={clearAllFilters}
                    className="inline-block bg-zinc-900 text-white px-8 py-3 uppercase tracking-widest text-xs hover:bg-zinc-800 transition-colors"
                  >
                    Clear All Filters
                  </button>
                </div>
              )

            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                {sortedProducts.map((product) => (
                  <ProductCard key={product.product_id} product={product} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />

      {/* ── Mobile filter drawer ───────────────────────────────────────── */}
      {mobileFilterOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 lg:hidden"
          onClick={() => setMobileFilterOpen(false)}
          data-testid="filter-backdrop"
        />
      )}

      <div
        className={`fixed top-0 left-0 h-full w-72 bg-white z-50 shadow-2xl transform transition-transform duration-300 lg:hidden ${
          mobileFilterOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        data-testid="mobile-filter-drawer"
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-zinc-100">
          <div className="flex items-center gap-2">
            <Filter size={16} className="text-zinc-400" />
            <h2 className="text-xs uppercase tracking-widest font-medium">
              Filters {activeFilterCount > 0 && `(${activeFilterCount})`}
            </h2>
          </div>
          <button
            onClick={() => setMobileFilterOpen(false)}
            className="p-1 hover:bg-zinc-100 transition-colors"
            aria-label="Close filters"
          >
            <X size={18} />
          </button>
        </div>
        <div className="px-6 py-6 overflow-y-auto h-full pb-24">
          <FilterContent />
        </div>
      </div>
    </div>
  );
}

// ── Product card ──────────────────────────────────────────────────────────────

function ProductCard({ product }) {
  const [hovered, setHovered] = useState(false);

  const primaryImage   = resolveImageUrl(product.image_url);
  const secondaryImage = product.image_url_2 ? resolveImageUrl(product.image_url_2) : null;

  const discountPct = product.compare_at_price > product.price
    ? Math.round(((product.compare_at_price - product.price) / product.compare_at_price) * 100)
    : 0;

  return (
    <Link
      to={`/product/${product.product_id}`}
      className="group block"
      data-testid={`product-card-${product.product_id}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Image container */}
      <div className="relative overflow-hidden bg-zinc-100 mb-4 aspect-[4/5]">

        <img
          src={primaryImage}
          alt={product.name}
          loading="lazy"
          className={`absolute inset-0 w-full h-full object-cover transition-all duration-500 ${
            hovered && secondaryImage ? "opacity-0 scale-105" : "opacity-100 scale-100 group-hover:scale-105"
          }`}
        />

        {secondaryImage && (
          <img
            src={secondaryImage}
            alt={`${product.name} alternate view`}
            loading="lazy"
            className={`absolute inset-0 w-full h-full object-cover transition-all duration-500 ${
              hovered ? "opacity-100 scale-100" : "opacity-0 scale-105"
            }`}
          />
        )}

        {/* Badges — top-left stack */}
        <div className="absolute top-2 left-2 flex flex-col gap-1 z-10">
          {product.is_best_seller && (
            <span
              className="bg-zinc-900 text-white text-[10px] px-2 py-0.5 uppercase tracking-wide"
              data-testid="best-seller-badge"
            >
              Best Seller
            </span>
          )}
          {discountPct > 0 && (
            <span className="bg-[#D4AF37] text-white text-[10px] px-2 py-0.5 uppercase tracking-wide">
              {discountPct}% off
            </span>
          )}
        </div>

        {/* Out of stock overlay */}
        {product.stock === 0 && (
          <div className="absolute inset-0 bg-white/60 flex items-center justify-center">
            <span className="text-xs uppercase tracking-widest text-zinc-500 bg-white px-3 py-1 border border-zinc-200">
              Out of Stock
            </span>
          </div>
        )}

        {/* Low stock badge — top-right */}
        {product.stock > 0 && product.stock <= 5 && (
          <div
            className="absolute top-2 right-2 bg-red-600 text-white text-[10px] px-2 py-0.5 uppercase tracking-wide z-10"
            data-testid="low-stock-badge"
          >
            Only {product.stock} left
          </div>
        )}
      </div>

      {/* Product info */}
      <h3 className="text-lg sm:text-xl font-light cormorant mb-0.5 group-hover:text-zinc-600 transition-colors">
        {product.name}
      </h3>
      <p className="text-xs text-zinc-400 uppercase tracking-wide mb-2">{product.category}</p>
      <div className="flex items-center gap-2 flex-wrap">
        <p className="text-sm font-medium">₹{product.price.toLocaleString()}</p>
        {discountPct > 0 && (
          <p className="text-xs text-zinc-400 line-through">
            ₹{product.compare_at_price.toLocaleString()}
          </p>
        )}
        {discountPct > 0 && (
          <p className="text-xs text-green-600 font-medium">{discountPct}% off</p>
        )}
      </div>
    </Link>
  );
}
